import * as React from "react";
import {
  Box,
  Grid,
  InputLabel,
  Link,
  Radio,
  Select,
  TextField,
} from "@mui/material";
import { SecondaryInsuranceType } from "../types";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { ImagesContext } from "../../Context";
import globals from "../../globals/globals";
import moment from "moment";
import InputMask from "react-input-mask";

interface SecondaryInsuranceEditProps {
  secondaryInsurance: SecondaryInsuranceType | null;
  insuranceTypeOptions: any;
  handleSecondaryInsuranceChange: (
    updated: Partial<SecondaryInsuranceType>
  ) => void;
}

const SecondaryInsuranceEdit: React.FC<SecondaryInsuranceEditProps> = ({
  secondaryInsurance,
  insuranceTypeOptions,
  handleSecondaryInsuranceChange,
}) => {
  const images = React.useContext(ImagesContext);
  const [openDateOfBirth, setOpenDateOfBirth] = React.useState<boolean>(false);

  const stateOptions = globals.states.map((state) => {
    return (
      <option key={state} value={state}>
        {state}
      </option>
    );
  });

  const insuranceTypes = insuranceTypeOptions?.map((option) => {
    return (
      <option key={option?.insurance_type} value={option?.insurance_type}>
        {option?.insurance_type}
      </option>
    );
  });

  const handleDateOfBirthChange = (date: moment.Moment | null) => {
    handleSecondaryInsuranceChange({
      insured_dob: date ? date.format("YYYY-MM-DD") : "",
    });
  };

  return (
    <Box className="edit-container">
      <Grid item xs={12} className="claim-edit-container">
        <Grid container>
          <Grid container className="claim-form-container">
            <Grid item xs={2.5} sx={{ mr: 5 }}>
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="last_name" className="field-label">
                    Insurance Type*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <Select
                    id="insurance_type"
                    value={secondaryInsurance?.insurance_type ?? ""}
                    required
                    variant="outlined"
                    className="the-field"
                    onChange={(e) =>
                      handleSecondaryInsuranceChange({
                        insurance_type: e.target.value,
                      })
                    }
                    native
                  >
                    <option value={""}>Select Insurance</option>
                    {insuranceTypes}
                  </Select>
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="first_name" className="field-label">
                    Insurance Plan or Program*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="first_name"
                    size="small"
                    value={secondaryInsurance?.plan_name ?? ""}
                    className="the-field"
                    required
                    autoComplete="new-password"
                    variant="outlined"
                    onChange={(e) =>
                      handleSecondaryInsuranceChange({
                        plan_name: e.target.value,
                      })
                    }
                  />
                </Grid>
              </Grid>

              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="middle_name" className="field-label">
                    Insurance Number*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="middle_name"
                    size="small"
                    value={secondaryInsurance?.insurance_number ?? ""}
                    className="the-field"
                    required
                    autoComplete="off"
                    variant="outlined"
                    onChange={(e) =>
                      handleSecondaryInsuranceChange({
                        insurance_number: e.target.value,
                      })
                    }
                  />
                </Grid>
              </Grid>

              <Grid container className="field-container">
                <Grid item xs={12}>
                  <InputLabel htmlFor="gender" className="field-label">
                    Patient’s Relationship to Insured*
                  </InputLabel>
                </Grid>
                <Grid item xs={12}>
                  <div className="row" style={{ height: 40 }}>
                    <Radio
                      checked={secondaryInsurance?.relationship === "self"}
                      onChange={(e) =>
                        handleSecondaryInsuranceChange({ relationship: "self" })
                      }
                      value="self"
                      name="radio-buttons"
                      inputProps={{ "aria-label": "Self" }}
                      style={{ backgroundColor: "transparent" }}
                      sx={{
                        color: "#A29D9B",
                        ml: 2,
                        mr: 1,
                        width: 15,
                        height: 15,
                      }}
                    />
                    <p className="radio-label">Self</p>
                    <Radio
                      checked={secondaryInsurance?.relationship === "spouse"}
                      onChange={(e) =>
                        handleSecondaryInsuranceChange({
                          relationship: "spouse",
                        })
                      }
                      value="spouse"
                      name="radio-buttons"
                      inputProps={{ "aria-label": "Spouse" }}
                      style={{ backgroundColor: "transparent" }}
                      sx={{
                        color: "#A29D9B",
                        ml: 2,
                        mr: 1,
                        width: 15,
                        height: 15,
                      }}
                    />
                    <p className="radio-label">Spouse</p>
                    <Radio
                      checked={secondaryInsurance?.relationship === "child"}
                      onChange={(e) =>
                        handleSecondaryInsuranceChange({
                          relationship: "child",
                        })
                      }
                      value="child"
                      name="radio-buttons"
                      inputProps={{ "aria-label": "Child" }}
                      style={{ backgroundColor: "transparent" }}
                      sx={{
                        color: "#A29D9B",
                        ml: 2,
                        mr: 1,
                        width: 15,
                        height: 15,
                      }}
                    />
                    <p className="radio-label">Child</p>
                    <Radio
                      checked={secondaryInsurance?.relationship === "other"}
                      onChange={(e) =>
                        handleSecondaryInsuranceChange({
                          relationship: "other",
                        })
                      }
                      value="other"
                      name="radio-buttons"
                      inputProps={{ "aria-label": "Other" }}
                      style={{ backgroundColor: "transparent" }}
                      sx={{
                        color: "#A29D9B",
                        ml: 2,
                        mr: 1,
                        width: 15,
                        height: 15,
                      }}
                    />
                    <p className="radio-label">Other</p>
                  </div>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={2.5} sx={{ mr: 5 }}>
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="last_name" className="field-label">
                    Insured’s Last Name*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="last_name"
                    size="small"
                    value={secondaryInsurance?.last_name ?? ""}
                    className="the-field"
                    required
                    autoComplete="new-password"
                    variant="outlined"
                    onChange={(e) =>
                      handleSecondaryInsuranceChange({
                        last_name: e.target.value,
                      })
                    }
                  />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="first_name" className="field-label">
                    Insured’s First Name*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="first_name"
                    size="small"
                    value={secondaryInsurance?.first_name ?? ""}
                    className="the-field"
                    required
                    autoComplete="new-password"
                    variant="outlined"
                    onChange={(e) =>
                      handleSecondaryInsuranceChange({
                        first_name: e.target.value,
                      })
                    }
                  />
                </Grid>
              </Grid>

              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="middle_name" className="field-label">
                    Insured’s Middle Name*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="middle_name"
                    size="small"
                    value={secondaryInsurance?.middle_name ?? ""}
                    className="the-field"
                    required
                    autoComplete="off"
                    variant="outlined"
                    onChange={(e) =>
                      handleSecondaryInsuranceChange({
                        middle_name: e.target.value,
                      })
                    }
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={2.5} sx={{ mr: 5 }}>
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="address" className="field-label">
                    Address Line One*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="address"
                    size="small"
                    value={secondaryInsurance?.address_line_1 ?? ""}
                    required
                    autoComplete="new-password"
                    variant="outlined"
                    className="the-field"
                    onChange={(e) =>
                      handleSecondaryInsuranceChange({
                        address_line_1: e.target.value,
                      })
                    }
                  />
                </Grid>
              </Grid>

              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="address" className="field-label">
                    Address Line Two
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="address"
                    size="small"
                    value={secondaryInsurance?.address_line_2 ?? ""}
                    required
                    autoComplete="new-password"
                    variant="outlined"
                    className="the-field"
                    onChange={(e) =>
                      handleSecondaryInsuranceChange({
                        address_line_2: e.target.value,
                      })
                    }
                  />
                </Grid>
              </Grid>

              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="city" className="field-label">
                    City*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="city"
                    size="small"
                    value={secondaryInsurance?.city ?? ""}
                    required
                    autoComplete="new-password"
                    variant="outlined"
                    className="the-field"
                    onChange={(e) =>
                      handleSecondaryInsuranceChange({ city: e.target.value })
                    }
                  />
                </Grid>
              </Grid>

              <Grid container justifyContent="space-between">
                <Grid container xs={5.5}>
                  <Grid item xs={12}>
                    <InputLabel htmlFor="state" className="field-label">
                      State*
                    </InputLabel>
                  </Grid>
                  <Grid item xs={12} className="field-container">
                    <Select
                      id="state"
                      value={secondaryInsurance?.state || ""}
                      required
                      variant="outlined"
                      className="the-field"
                      onChange={(e) =>
                        handleSecondaryInsuranceChange({
                          state: e.target.value,
                        })
                      }
                      native
                    >
                      <option value={""}>Select</option>
                      {stateOptions}
                    </Select>
                  </Grid>
                </Grid>
                <Grid container xs={5.5} sx={{ mr: 1 }}>
                  <Grid item xs={12}>
                    <InputLabel htmlFor="zip" className="field-label">
                      ZIP Code*
                    </InputLabel>
                  </Grid>
                  <Grid item xs={12} className="field-container">
                    <TextField
                      id="zip"
                      placeholder="Enter Zip"
                      size="small"
                      value={secondaryInsurance?.zip || ""}
                      required
                      autoComplete="new-password"
                      variant="outlined"
                      className="the-field"
                      onChange={(e) => {
                        const zipCode = e.target.value.replace(/\D/g, "");
                        handleSecondaryInsuranceChange({ zip: zipCode });
                      }}
                      slotProps={{
                        htmlInput: {
                          inputMode: "numeric",
                          maxLength: 5,
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={2.5} sx={{ mr: 5 }}>
              <Box>
                <InputLabel htmlFor="dateOfBirth" className="field-label">
                  Date of Birth*
                </InputLabel>
                <Box
                  className="field-container"
                  sx={{
                    marginLeft: "0px",
                    backgroundColor: "#efe9e7",
                    display: "flex",
                    borderRadius: "5px",
                    paddingRight: "10.5px",
                  }}
                >
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <Box
                      className="date-box"
                      sx={{
                        width: "88%",
                        maxHeight: 41.13,
                      }}
                    >
                      <DemoContainer components={["DatePicker", "DatePicker"]}>
                        <DatePicker
                          open={openDateOfBirth}
                          onOpen={() => setOpenDateOfBirth(true)}
                          onClose={() => setOpenDateOfBirth(false)}
                          value={
                            secondaryInsurance?.insured_dob
                              ? moment(secondaryInsurance.insured_dob)
                              : null
                          }
                          onChange={(x) => {
                            handleDateOfBirthChange(x);
                          }}
                          format="MM/DD/YYYY"
                          disableOpenPicker
                          disabled={false}
                          slotProps={{
                            textField: {
                              placeholder: "Date of Birth",
                            },
                            openPickerIcon: images.calendar_icon,
                          }}
                          className="report-date-input no-border-outline"
                          maxDate={null}
                        />
                      </DemoContainer>
                    </Box>
                  </LocalizationProvider>
                  <Link
                    className="invite-app-user-link"
                    onClick={() => setOpenDateOfBirth(true)}
                  >
                    <img
                      src={images.calendar_icon}
                      alt="Calendar"
                      className="action-icon-image"
                      style={{ width: 22.75, maxHeight: 26 }}
                    />
                  </Link>
                </Box>
              </Box>

              <Grid container>
                <Grid item xs={12}>
                  <InputLabel
                    htmlFor="mobile_phone_number"
                    className="field-label"
                  >
                    Phone Number*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <InputMask
                    id="mobile-phone-number"
                    mask="999-999-9999"
                    value={secondaryInsurance?.phone_number}
                    disabled={false}
                    maskChar=""
                    variant="outlined"
                    className="the-field"
                    size="small"
                    onChange={(e) => {
                      handleSecondaryInsuranceChange({
                        phone_number: e.target.value,
                      });
                    }}
                  >
                    {() => (
                      <TextField
                        variant="outlined"
                        size="small"
                        className="the-field"
                        autoComplete="off"
                      />
                    )}
                  </InputMask>
                </Grid>
              </Grid>

              <Grid container className="field-container">
                <Grid item xs={12}>
                  <InputLabel htmlFor="gender" className="field-label">
                    Sex*
                  </InputLabel>
                </Grid>
                <Grid item xs={12}>
                  <div className="row" style={{ height: 40 }}>
                    <Radio
                      checked={secondaryInsurance?.gender === "Male"}
                      onChange={(e) =>
                        handleSecondaryInsuranceChange({ gender: "Male" })
                      }
                      value="Male"
                      name="radio-buttons"
                      inputProps={{ "aria-label": "Male" }}
                      style={{ backgroundColor: "transparent" }}
                      sx={{
                        color: "#A29D9B",
                        ml: 1,
                        width: 32,
                        height: 32,
                      }}
                    />
                    <p className="radio-label">Male</p>
                    <Radio
                      checked={secondaryInsurance?.gender === "Female"}
                      onChange={(e) =>
                        handleSecondaryInsuranceChange({ gender: "Female" })
                      }
                      value="Female"
                      name="radio-buttons"
                      inputProps={{ "aria-label": "Female" }}
                      style={{ backgroundColor: "transparent" }}
                      sx={{
                        color: "#A29D9B",
                        ml: 2,
                        width: 32,
                        height: 32,
                      }}
                    />
                    <p className="radio-label">Female</p>
                  </div>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SecondaryInsuranceEdit;
